import { useState, useCallback } from "react";
import { FiExternalLink, FiDownload } from "react-icons/fi";
import { Spinner } from "@chakra-ui/react";
import { cn } from "utils/helpers";
import { generatePdf } from "components/RFQ/PdfDownloadButton/api-PdfDownload";
import type { SupplierEvaluationSummary, SupplierEvaluationStatus } from "../../../types/scoring.type";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";

interface ScoringModalHeaderProps {
	suppliers: SupplierEvaluationSummary[];
	selectedInviteId: number;
	percentComplete: number;
	rfxId: number;
	readOnly?: boolean;
}

const getStatusLabel = (status: SupplierEvaluationStatus): string => {
	switch (status) {
		case "done":
			return "Done";
		case "in_progress":
			return "In Progress";
		case "not_started":
			return "Not Started";
		default:
			return status;
	}
};

const getStatusBadgeClasses = (status: SupplierEvaluationStatus): string => {
	switch (status) {
		case "done":
			return "bg-green-100 text-green-700";
		case "in_progress":
			return "bg-blue-100 text-blue-700";
		case "not_started":
			return "bg-gray-100 text-gray-600";
		default:
			return "bg-gray-100 text-gray-600";
	}
};

const formatDate = (dateStr: string | null): string => {
	if (!dateStr) {
		return "—";
	}
	return new Date(dateStr).toLocaleDateString("en-AU", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
};

export const ScoringModalHeader: React.FC<ScoringModalHeaderProps> = ({
	suppliers,
	selectedInviteId,
	percentComplete,
	rfxId,
}) => {
	const currentSupplier = suppliers.find((s) => s.inviteId === selectedInviteId);
	const { clientPaths } = useNavigationPaths();
	const [isPdfLoading, setIsPdfLoading] = useState(false);

	const handlePdfDownload = useCallback(() => {
		setIsPdfLoading(true);
		generatePdf("responses", rfxId, selectedInviteId, false).finally(() => {
			setIsPdfLoading(false);
		});
	}, [rfxId, selectedInviteId]);

	return (
		<div className="space-y-3" data-testid="scoring-modal-header">
			<div className="flex flex-wrap items-center gap-4">
				{currentSupplier && (
					<>
						<span
							className={cn(
								"inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
								getStatusBadgeClasses(currentSupplier.status)
							)}
						>
							{getStatusLabel(currentSupplier.status)}
						</span>

						<span className="text-xs text-gray-500">
							Started: {formatDate(currentSupplier.startedAt)}
						</span>
						<span className="text-xs text-gray-500">
							Completed: {formatDate(currentSupplier.completedAt)}
						</span>
					</>
				)}

				<div className="ml-auto flex items-center gap-2">
					<a
						href={clientPaths.viewRfxSubmission(rfxId, selectedInviteId)}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
						data-testid="view-response-link"
					>
						<FiExternalLink size={12} />
						View Response
					</a>
					<button
						type="button"
						onClick={handlePdfDownload}
						disabled={isPdfLoading}
						className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
						data-testid="download-pdf-btn"
					>
						{isPdfLoading ? (
							<Spinner size="xs" />
						) : (
							<FiDownload size={12} />
						)}
						{isPdfLoading ? "Generating..." : "PDF"}
					</button>
				</div>
			</div>

			<div className="w-full" data-testid="progress-bar-container">
				<div className="flex items-center justify-between mb-1">
					<span className="text-xs text-gray-500">Progress</span>
					<span className="text-xs font-medium text-gray-700">{percentComplete}%</span>
				</div>
				<div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
					<div
						className={cn(
							"h-full rounded-full transition-all duration-300",
							percentComplete === 100 ? "bg-green-500" : "bg-blue-500"
						)}
						style={{ width: `${percentComplete}%` }}
						data-testid="progress-bar"
					/>
				</div>
			</div>
		</div>
	);
};
